import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Calendar, Clock, User, Phone, Mail, MapPin, FileText, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const API = `${typeof window !== "undefined" ? window.location.origin : ""}/api`;
const ALL_HOURS = Array.from({ length: 12 }, (_, i) => `${String(i + 9).padStart(2, "0")}:00`); // 9h-20h

export function RdvAdminModal({ open, onClose, defaultValues = {}, etudeId = null, dossierId = null, onCreated, simulationData = null }) {
  const [form, setForm] = useState({
    client_nom: defaultValues.nom || "",
    client_telephone: defaultValues.telephone || "",
    client_email: defaultValues.email || "",
    client_adresse: defaultValues.adresse || "",
    programme: defaultValues.programme || "",
    notes: defaultValues.notes || "",
    date: "",
    heure: "",
    attribue_a: "",
  });
  const [saving, setSaving] = useState(false);
  const [adminUsersList, setAdminUsersList] = useState([]);
  const [busySlots, setBusySlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  // Fetch admin users — prioritize assigned admin for current user
  useEffect(() => {
    const fetchAdmins = async () => {
      try {
        const currentUser = localStorage.getItem("label_energie_user") || "";
        // Try to get assigned admin first
        const assignedRes = await fetch(`${API}/users/assigned-admin/${encodeURIComponent(currentUser)}`);
        const assignedData = await assignedRes.json();
        
        // Also get all admin users as fallback
        const allRes = await fetch(`${API}/users`);
        const allUsers = await allRes.json();
        const allAdmins = allUsers.filter(u => u.role === "admin_full" || u.role === "admin_restricted" || u.role === "administratif");
        
        // Put assigned admins first, then others
        const assignedUsernames = new Set((assignedData.admins || []).map(a => a.username));
        const orderedAdmins = [
          ...(assignedData.admins || []),
          ...allAdmins.filter(a => !assignedUsernames.has(a.username)),
        ];
        
        setAdminUsersList(orderedAdmins);
        if (orderedAdmins.length > 0 && !form.attribue_a) {
          setForm(p => ({ ...p, attribue_a: orderedAdmins[0].username }));
        }
      } catch (err) {
        // Fallback to all users
        try {
          const res = await fetch(`${API}/users`);
          const users = await res.json();
          const admins = users.filter(u => u.role === "admin_full" || u.role === "admin_restricted" || u.role === "administratif");
          setAdminUsersList(admins);
          if (admins.length > 0 && !form.attribue_a) {
            setForm(p => ({ ...p, attribue_a: admins[0].username }));
          }
        } catch (err) {}
      }
    };
    if (open) fetchAdmins();
    // eslint-disable-next-line
  }, [open]);

  // Fetch busy slots when date or admin changes
  useEffect(() => {
    if (!form.date || !form.attribue_a) { setBusySlots([]); return; }
    const fetchBusy = async () => {
      setLoadingSlots(true);
      try {
        const res = await fetch(`${API}/planning/busy-slots/${encodeURIComponent(form.attribue_a)}?date=${form.date}`);
        const data = await res.json();
        setBusySlots(data.busy_slots || []);
      } catch (err) { setBusySlots([]); }
      setLoadingSlots(false);
    };
    fetchBusy();
  }, [form.date, form.attribue_a]);

  // Available hours (exclude busy slots)
  const availableHours = ALL_HOURS.filter(h => !busySlots.includes(h));

  // Auto-select first available hour when date changes
  useEffect(() => {
    if (availableHours.length > 0 && !availableHours.includes(form.heure)) {
      setForm(p => ({ ...p, heure: availableHours[0] }));
    }
  }, [availableHours]); // eslint-disable-line

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // Check if selected date is a weekend
  const isWeekend = (dateStr) => {
    if (!dateStr) return false;
    const d = new Date(dateStr + "T12:00:00");
    return d.getDay() === 0 || d.getDay() === 6;
  };

  const handleCreate = async () => {
    if (!form.client_nom.trim()) { toast.error("Le nom du client est requis"); return; }
    if (!form.date) { toast.error("La date est requise"); return; }
    if (!form.heure) { toast.error("Veuillez sélectionner un créneau disponible"); return; }
    if (isWeekend(form.date)) { toast.error("Les RDV ne peuvent être planifiés que du lundi au vendredi"); return; }
    setSaving(true);
    try {
      // Step 1: Create a dossier for this RDV admin
      let createdDossierId = dossierId || null;
      if (!createdDossierId) {
        try {
          const dossierPayload = {
            client_name: form.client_nom,
            client_email: form.client_email || "",
            client_phone: form.client_telephone || "",
            profile: "proprietaire_occupant",
            programme: form.programme === "Cumulée" ? "cumule" : form.programme === "174" ? "174" : "MPR",
            zone: simulationData?.zone || "H1",
            metrage: simulationData?.surface ? parseFloat(simulationData.surface) : 0,
            precarite: simulationData?.precarite || "",
            code_postal: "",
            adresse_logement: form.client_adresse || "",
            notes: `RDV Admin le ${form.date} à ${form.heure} avec ${form.attribue_a}. ${form.notes || ""}`.trim(),
            statut_dossier: "rdv_admin",
            assigned_to: localStorage.getItem("label_energie_user") || "",
            etude_id: etudeId || null,
          };
          const dossierRes = await fetch(`${API}/dossiers`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(dossierPayload),
          });
          if (dossierRes.ok) {
            const dossierData = await dossierRes.json();
            createdDossierId = dossierData.id;
          }
        } catch (dossierErr) {
        }
      }

      // Step 2: Link etude back to dossier if both exist
      if (etudeId && createdDossierId) {
        try {
          await fetch(`${API}/etudes/${etudeId}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ admin_dossier_id: createdDossierId }),
          });
        } catch (err) {}
      }

      // Step 3: Create the RDV with the dossier_id
      const res = await fetch(`${API}/planning/rdvs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          date: form.date,
          heure: form.heure,
          attribue_a: form.attribue_a || localStorage.getItem("label_energie_user") || "",
          client_nom: form.client_nom,
          client_telephone: form.client_telephone,
          client_adresse: form.client_adresse,
          programme: form.programme,
          notes: form.notes,
          created_by: localStorage.getItem("label_energie_user") || "",
          rdv_type: "rdv_admin",
          etude_id: etudeId || null,
          dossier_id: createdDossierId,
        }),
      });
      if (!res.ok) throw new Error();
      toast.success(createdDossierId ? "RDV planifié et dossier créé !" : "RDV planifié avec succès !");
      onCreated?.(createdDossierId);
      onClose();
    } catch (err) { toast.error("Erreur lors de la création du RDV"); }
    setSaving(false);
  };

  const today = new Date().toISOString().split("T")[0];
  const weekendWarning = isWeekend(form.date);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="rounded-xl max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-500" />
            Planifier un RDV Admin
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* Admin selection */}
          <div>
            <label className="text-xs font-semibold flex items-center gap-1"><User className="w-3 h-3" />RDV avec *</label>
            <select value={form.attribue_a} onChange={e => set("attribue_a", e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-sm" data-testid="select-rdv-attribue">
              {adminUsersList.map(u => <option key={u.username} value={u.username}>{u.username}</option>)}
            </select>
          </div>

          {/* Date and time */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold flex items-center gap-1"><Calendar className="w-3 h-3" />Date *</label>
              <input type="date" value={form.date} min={today} onChange={e => set("date", e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-sm" data-testid="input-rdv-date" />
              {weekendWarning && (
                <p className="text-[10px] text-red-500 mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" /> Week-end : pas de RDV
                </p>
              )}
            </div>
            <div>
              <label className="text-xs font-semibold flex items-center gap-1"><Clock className="w-3 h-3" />Créneau *</label>
              {loadingSlots ? (
                <div className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-muted text-sm text-muted-foreground">Chargement...</div>
              ) : form.date && !weekendWarning ? (
                availableHours.length > 0 ? (
                  <select value={form.heure} onChange={e => set("heure", e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-sm" data-testid="input-rdv-heure">
                    {availableHours.map(h => <option key={h} value={h}>{h}</option>)}
                  </select>
                ) : (
                  <div className="w-full mt-1 px-3 py-2 rounded-lg border border-red-500/30 bg-red-500/10 text-sm text-red-600">
                    Aucun créneau libre
                  </div>
                )
              ) : (
                <div className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-muted text-sm text-muted-foreground">
                  Sélectionnez une date
                </div>
              )}
              {form.date && !weekendWarning && busySlots.length > 0 && (
                <p className="text-[10px] text-amber-600 mt-1">{busySlots.length} créneau(x) occupé(s)</p>
              )}
            </div>
          </div>

          <div className="border-t border-border pt-3 space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase">Coordonnées client</p>
            <div>
              <label className="text-xs font-semibold flex items-center gap-1"><User className="w-3 h-3" />Nom *</label>
              <input type="text" value={form.client_nom} onChange={e => set("client_nom", e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-sm" placeholder="Nom complet" data-testid="input-rdv-modal-nom" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold flex items-center gap-1"><Phone className="w-3 h-3" />Téléphone</label>
                <input type="tel" value={form.client_telephone} onChange={e => set("client_telephone", e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-sm" placeholder="06 ..." data-testid="input-rdv-modal-tel" />
              </div>
              <div>
                <label className="text-xs font-semibold flex items-center gap-1"><Mail className="w-3 h-3" />Email</label>
                <input type="email" value={form.client_email} onChange={e => set("client_email", e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-sm" placeholder="email@..." data-testid="input-rdv-modal-email" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold flex items-center gap-1"><MapPin className="w-3 h-3" />Adresse</label>
              <input type="text" value={form.client_adresse} onChange={e => set("client_adresse", e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-sm" placeholder="Adresse complète" data-testid="input-rdv-modal-adresse" />
            </div>
          </div>

          <div className="border-t border-border pt-3 space-y-3">
            <div>
              <label className="text-xs font-semibold flex items-center gap-1"><FileText className="w-3 h-3" />Programme</label>
              <select value={form.programme} onChange={e => set("programme", e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-sm" data-testid="select-rdv-modal-programme">
                <option value="">Sélectionner...</option>
                <option value="174">Programme 174</option>
                <option value="MPR">Programme MPR</option>
                <option value="Cumulée">Opération Cumulée</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold">Notes</label>
              <textarea value={form.notes} onChange={e => set("notes", e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-sm resize-none" rows={2} placeholder="Notes..." data-testid="input-rdv-modal-notes" />
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={onClose} className="flex-1 rounded-lg">Annuler</Button>
            <Button onClick={handleCreate} disabled={saving || !form.client_nom.trim() || !form.date || !form.heure || weekendWarning} className="flex-1 rounded-lg bg-blue-500 hover:bg-blue-600 text-white" data-testid="btn-confirm-rdv-modal">
              {saving ? "Création..." : "Planifier le RDV"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
